<template>
  <div class="contact-submissions-container">
    <div class="header">
      <div>
        <h2>Contact Submissions</h2>
        <div class="subtitle">Review user reports and grant subscription-day rewards.</div>
      </div>
      <el-button size="small" icon="el-icon-refresh" @click="fetchData">Refresh</el-button>
    </div>

    <el-form :inline="true" size="small" class="filters" @submit.native.prevent="search">
      <el-form-item label="Type">
        <el-select v-model="filters.contact_type" placeholder="All" style="width: 190px" @change="search">
          <el-option label="All" value="all" />
          <el-option
            v-for="type in contactTypes"
            :key="type.value"
            :label="type.label"
            :value="type.value"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="Reward">
        <el-select v-model="filters.reward_status" placeholder="All" style="width: 150px" @change="search">
          <el-option label="All" value="all" />
          <el-option label="Not reviewed" value="not_reviewed" />
          <el-option label="Awarded" value="awarded" />
          <el-option label="Rejected" value="rejected" />
          <el-option label="Ineligible" value="ineligible" />
        </el-select>
      </el-form-item>
      <el-form-item label="Status">
        <el-select v-model="filters.status" placeholder="All" style="width: 130px" @change="search">
          <el-option label="All" value="all" />
          <el-option label="New" value="new" />
          <el-option label="Closed" value="closed" />
        </el-select>
      </el-form-item>
      <el-form-item label="Date">
        <el-date-picker
          v-model="filters.dateRange"
          type="daterange"
          range-separator="-"
          start-placeholder="Start"
          end-placeholder="End"
          value-format="yyyy-MM-dd"
          style="width: 250px"
          @change="search"
        />
      </el-form-item>
      <el-form-item>
        <el-input
          v-model="filters.search"
          clearable
          placeholder="Email, name, subject, message"
          style="width: 280px"
          @clear="search"
          @keyup.enter.native="search"
        >
          <el-button slot="append" icon="el-icon-search" @click="search" />
        </el-input>
      </el-form-item>
    </el-form>

    <el-table
      v-loading="loading"
      :data="items"
      border
      size="small"
      style="width: 100%"
    >
      <el-table-column label="Time" width="150">
        <template slot-scope="scope">
          {{ formatDate(scope.row.create_time) }}
        </template>
      </el-table-column>
      <el-table-column label="Type" width="170">
        <template slot-scope="scope">
          <el-tag size="small" type="info">{{ scope.row.contact_type_label || scope.row.contact_type || '-' }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="Message" min-width="300">
        <template slot-scope="scope">
          <div class="subject">{{ scope.row.subject || '-' }}</div>
          <div class="message-preview">{{ scope.row.message || '-' }}</div>
        </template>
      </el-table-column>
      <el-table-column label="User" min-width="220">
        <template slot-scope="scope">
          <div class="user-main">{{ scope.row.user_email || '-' }}</div>
          <div class="muted">{{ scope.row.user_name || scope.row.user_id || '-' }}</div>
        </template>
      </el-table-column>
      <el-table-column label="Eligibility" width="190">
        <template slot-scope="scope">
          <el-tag :type="eligibilityTagType(scope.row.eligibility)" size="small">
            {{ eligibilityText(scope.row.eligibility) }}
          </el-tag>
          <div v-if="scope.row.eligibility && scope.row.eligibility.active_subscription_end_date" class="muted tiny">
            until {{ formatDate(scope.row.eligibility.active_subscription_end_date) }}
          </div>
        </template>
      </el-table-column>
      <el-table-column label="Reward" width="150">
        <template slot-scope="scope">
          <el-tag :type="rewardTagType(scope.row.reward_status)" size="small">
            {{ rewardText(scope.row.reward_status) }}
          </el-tag>
          <div v-if="scope.row.reward_days" class="muted tiny">+{{ scope.row.reward_days }} days</div>
        </template>
      </el-table-column>
      <el-table-column label="Actions" width="250" fixed="right">
        <template slot-scope="scope">
          <el-button size="mini" @click="openDetail(scope.row)">Detail</el-button>
          <el-dropdown
            size="mini"
            split-button
            type="primary"
            :disabled="!canAward(scope.row)"
            @click="openAward(scope.row, 7)"
            @command="handleAwardCommand"
          >
            Award 7d
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item :command="{ row: scope.row, days: 3 }">Award 3 days</el-dropdown-item>
              <el-dropdown-item :command="{ row: scope.row, days: 7 }">Award 7 days</el-dropdown-item>
              <el-dropdown-item :command="{ row: scope.row, days: 14 }">Award 14 days</el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
          <el-button
            size="mini"
            type="text"
            :disabled="isAwarded(scope.row)"
            @click="openReject(scope.row)"
          >
            Reject
          </el-button>
        </template>
      </el-table-column>
    </el-table>

    <div class="pagination">
      <el-pagination
        background
        layout="total, sizes, prev, pager, next"
        :current-page.sync="page"
        :page-size.sync="pageSize"
        :page-sizes="[10, 20, 50, 100]"
        :total="total"
        @size-change="fetchData"
        @current-change="fetchData"
      />
    </div>

    <el-dialog
      title="Contact Submission"
      :visible.sync="detailVisible"
      width="760px"
    >
      <div v-loading="detailLoading">
        <template v-if="detailItem">
          <div class="detail-section">
            <div class="detail-title">{{ detailItem.subject || '-' }}</div>
            <div class="detail-meta">
              {{ detailItem.contact_type_label || detailItem.contact_type || '-' }} · {{ formatDate(detailItem.create_time) }}
            </div>
            <div class="detail-message">{{ detailItem.message || '-' }}</div>
          </div>

          <el-descriptions :column="2" size="small" border>
            <el-descriptions-item label="User email">{{ detailItem.user_email || '-' }}</el-descriptions-item>
            <el-descriptions-item label="User name">{{ detailItem.user_name || '-' }}</el-descriptions-item>
            <el-descriptions-item label="User ID">{{ detailItem.user_id || '-' }}</el-descriptions-item>
            <el-descriptions-item label="Platform">{{ detailItem.platform || '-' }}</el-descriptions-item>
            <el-descriptions-item label="Reward">{{ rewardText(detailItem.reward_status) }}</el-descriptions-item>
            <el-descriptions-item label="Eligibility">{{ eligibilityText(detailItem.eligibility) }}</el-descriptions-item>
          </el-descriptions>

          <div v-if="detailItem.review_note" class="detail-note">
            <strong>Review note:</strong> {{ detailItem.review_note }}
          </div>

          <div v-if="detailItem.context" class="context-list">
            <div class="context-title">Context</div>
            <div v-for="(value, key) in detailItem.context" :key="key" class="context-row">
              <span>{{ key }}</span>
              <em>{{ value || '-' }}</em>
            </div>
          </div>
        </template>
      </div>
    </el-dialog>

    <el-dialog
      title="Grant Contribution Reward"
      :visible.sync="awardVisible"
      width="520px"
      :close-on-click-modal="false"
    >
      <el-form label-width="110px" size="small">
        <el-form-item label="Reward days">
          <el-radio-group v-model="awardForm.reward_days">
            <el-radio-button :label="3">3</el-radio-button>
            <el-radio-button :label="7">7</el-radio-button>
            <el-radio-button :label="14">14</el-radio-button>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="Review note">
          <el-input
            v-model="awardForm.review_note"
            type="textarea"
            :rows="3"
            maxlength="1000"
            show-word-limit
            placeholder="Optional note for the award record and email."
          />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="awardVisible = false">Cancel</el-button>
        <el-button type="primary" :loading="awardLoading" @click="submitAward">Grant reward</el-button>
      </span>
    </el-dialog>

    <el-dialog
      title="Close Reward Review"
      :visible.sync="rejectVisible"
      width="520px"
      :close-on-click-modal="false"
    >
      <el-form label-width="110px" size="small">
        <el-form-item label="Result">
          <el-radio-group v-model="rejectForm.reward_status">
            <el-radio label="rejected">Rejected</el-radio>
            <el-radio label="ineligible">Ineligible</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="Review note">
          <el-input
            v-model="rejectForm.review_note"
            type="textarea"
            :rows="3"
            maxlength="1000"
            show-word-limit
            placeholder="Why no reward was granted."
          />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="rejectVisible = false">Cancel</el-button>
        <el-button type="primary" :loading="rejectLoading" @click="submitReject">Save review</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import {
  approveContactContributionAward,
  getContactFormSubmission,
  listContactFormSubmissions,
  rejectContactContributionAward
} from '@/apis/lfAdmin'

export default {
  name: 'ContactSubmissions',
  data() {
    return {
      loading: false,
      detailLoading: false,
      awardLoading: false,
      rejectLoading: false,
      items: [],
      total: 0,
      page: 1,
      pageSize: 20,
      detailVisible: false,
      awardVisible: false,
      rejectVisible: false,
      detailItem: null,
      activeRow: null,
      filters: {
        contact_type: 'all',
        reward_status: 'not_reviewed',
        status: 'all',
        search: '',
        dateRange: null
      },
      awardForm: {
        reward_days: 7,
        review_note: ''
      },
      rejectForm: {
        reward_status: 'rejected',
        review_note: ''
      },
      contactTypes: [
        { value: 'bug_report', label: 'Report a bug' },
        { value: 'account_issue', label: 'Account issue' },
        { value: 'content_request', label: 'Request new collection/content' },
        { value: 'educator_course_request', label: 'Custom course request' },
        { value: 'billing_subscription', label: 'Billing or subscription' },
        { value: 'partnership', label: 'Partnership' },
        { value: 'general', label: 'General question' }
      ]
    }
  },
  mounted() {
    this.fetchData()
  },
  methods: {
    search() {
      this.page = 1
      this.fetchData()
    },
    buildParams() {
      const params = {
        page: this.page,
        page_size: this.pageSize
      }
      if (this.filters.contact_type !== 'all') params.contact_type = this.filters.contact_type
      if (this.filters.reward_status !== 'all') params.reward_status = this.filters.reward_status
      if (this.filters.status !== 'all') params.status = this.filters.status
      if (this.filters.search.trim()) params.search = this.filters.search.trim()
      if (this.filters.dateRange && this.filters.dateRange.length === 2) {
        params.date_from = dayjs(this.filters.dateRange[0]).startOf('day').unix()
        params.date_to = dayjs(this.filters.dateRange[1]).endOf('day').unix()
      }
      return params
    },
    async fetchData() {
      this.loading = true
      try {
        const res = await listContactFormSubmissions(this.buildParams())
        if (res.data.code === 0) {
          const data = res.data.data || {}
          this.items = data.items || []
          this.total = Number(data.total || 0)
        } else {
          this.$message.error(res.data.msg || 'Failed to load contact submissions')
        }
      } catch (e) {
        this.$message.error('Failed to load contact submissions')
      } finally {
        this.loading = false
      }
    },
    async openDetail(row) {
      const contactId = this.extractId(row)
      if (!contactId) return
      this.detailVisible = true
      this.detailLoading = true
      this.detailItem = null
      try {
        const res = await getContactFormSubmission({ contact_id: contactId })
        if (res.data.code === 0) {
          this.detailItem = res.data.data
        } else {
          this.$message.error(res.data.msg || 'Failed to load detail')
        }
      } catch (e) {
        this.$message.error('Failed to load detail')
      } finally {
        this.detailLoading = false
      }
    },
    openAward(row, days) {
      if (!this.canAward(row)) return
      this.activeRow = row
      this.awardForm = {
        reward_days: Number(days) || 7,
        review_note: ''
      }
      this.awardVisible = true
    },
    handleAwardCommand(command) {
      if (!command) return
      this.openAward(command.row, command.days)
    },
    async submitAward() {
      if (!this.activeRow) return
      const contactId = this.extractId(this.activeRow)
      if (!contactId) return
      this.awardLoading = true
      try {
        const res = await approveContactContributionAward({
          contact_id: contactId,
          reward_days: this.awardForm.reward_days,
          review_note: this.awardForm.review_note,
          reviewer_name: 'LF Admin'
        })
        if (res.data.code === 0) {
          this.$message.success('Reward granted')
          this.awardVisible = false
          this.fetchData()
          if (this.detailVisible && this.detailItem) this.openDetail(this.activeRow)
        } else {
          this.$message.error(res.data.msg || 'Failed to grant reward')
        }
      } catch (e) {
        this.$message.error('Failed to grant reward')
      } finally {
        this.awardLoading = false
      }
    },
    openReject(row) {
      if (this.isAwarded(row)) return
      this.activeRow = row
      this.rejectForm = {
        reward_status: 'rejected',
        review_note: ''
      }
      this.rejectVisible = true
    },
    async submitReject() {
      if (!this.activeRow) return
      const contactId = this.extractId(this.activeRow)
      if (!contactId) return
      this.rejectLoading = true
      try {
        const res = await rejectContactContributionAward({
          contact_id: contactId,
          reward_status: this.rejectForm.reward_status,
          review_note: this.rejectForm.review_note,
          reviewer_name: 'LF Admin'
        })
        if (res.data.code === 0) {
          this.$message.success('Review saved')
          this.rejectVisible = false
          this.fetchData()
          if (this.detailVisible && this.detailItem) this.openDetail(this.activeRow)
        } else {
          this.$message.error(res.data.msg || 'Failed to save review')
        }
      } catch (e) {
        this.$message.error('Failed to save review')
      } finally {
        this.rejectLoading = false
      }
    },
    canAward(row) {
      if (this.isAwarded(row)) return false
      return !!(row && row.eligibility && row.eligibility.eligible)
    },
    isAwarded(row) {
      return row && row.reward_status === 'awarded'
    },
    eligibilityText(eligibility) {
      if (!eligibility) return 'Unknown'
      if (eligibility.eligible) {
        return eligibility.has_active_subscription ? 'Eligible active' : 'Eligible history'
      }
      const reasonMap = {
        user_not_found: 'User not found',
        no_successful_subscription_history: 'No paid history'
      }
      return reasonMap[eligibility.reason] || 'Not eligible'
    },
    eligibilityTagType(eligibility) {
      if (!eligibility) return 'info'
      if (eligibility.eligible) return 'success'
      return 'warning'
    },
    rewardText(status) {
      const map = {
        awarded: 'Awarded',
        rejected: 'Rejected',
        ineligible: 'Ineligible',
        not_reviewed: 'Not reviewed'
      }
      return map[status] || 'Not reviewed'
    },
    rewardTagType(status) {
      if (status === 'awarded') return 'success'
      if (status === 'rejected') return 'info'
      if (status === 'ineligible') return 'warning'
      return ''
    },
    formatDate(ts) {
      const value = Number(ts || 0)
      if (!value) return '-'
      return dayjs.unix(value).format('YYYY-MM-DD HH:mm')
    },
    extractId(obj) {
      if (!obj) return ''
      if (typeof obj === 'string') return obj
      const id = obj._id
      if (!id) return ''
      if (typeof id === 'string') return id
      return id.$oid || id.$id || (id.toString ? id.toString() : '')
    }
  }
}
</script>

<style scoped>
.contact-submissions-container {
  padding: 20px;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
}

.header h2 {
  margin: 0;
  color: #303133;
  font-size: 22px;
  line-height: 1.25;
}

.subtitle {
  margin-top: 4px;
  color: #909399;
  font-size: 13px;
}

.filters {
  padding: 12px 12px 0;
  margin-bottom: 14px;
  background: #fff;
  border: 1px solid #ebeef5;
}

.subject,
.user-main {
  color: #303133;
  font-weight: 600;
}

.message-preview {
  margin-top: 4px;
  color: #606266;
  font-size: 12px;
  line-height: 1.45;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.muted {
  margin-top: 3px;
  color: #909399;
  font-size: 12px;
}

.tiny {
  font-size: 11px;
}

.pagination {
  display: flex;
  justify-content: flex-end;
  margin-top: 14px;
}

.detail-section {
  margin-bottom: 16px;
}

.detail-title {
  color: #303133;
  font-size: 18px;
  font-weight: 700;
}

.detail-meta {
  margin-top: 4px;
  color: #909399;
  font-size: 12px;
}

.detail-message {
  margin-top: 12px;
  padding: 12px;
  color: #303133;
  line-height: 1.6;
  white-space: pre-wrap;
  background: #f7f8fb;
  border: 1px solid #ebeef5;
}

.detail-note {
  margin-top: 14px;
  padding: 10px 12px;
  color: #606266;
  background: #fdf6ec;
  border: 1px solid #faecd8;
}

.context-list {
  margin-top: 16px;
}

.context-title {
  margin-bottom: 8px;
  color: #303133;
  font-weight: 600;
}

.context-row {
  display: grid;
  grid-template-columns: 150px 1fr;
  gap: 10px;
  padding: 7px 0;
  border-bottom: 1px solid #f0f2f5;
  font-size: 12px;
}

.context-row span {
  color: #909399;
}

.context-row em {
  color: #606266;
  font-style: normal;
  word-break: break-word;
}
</style>
